import React, { useState } from 'react';
import { Modal, Badge, Input, Button } from '@/components/shared/ui';
import { CheckCircle } from 'lucide-react';
import { User } from '../../../types/admin';
import AlertDialog from '@/components/shared/ui/AlertDialog';
import { useRegularMemberList } from '@/hooks/useUserManagement';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userServiceApi } from '@/services/userServiceApi';

interface LicenseInfo {
    hasDeviceLicense: boolean;
    maxUsers: number | null;
    currentUsers: number;
    availableSlots?: number;
}

export interface MockMember {
    userId: number;
    name: string;
    loginId?: string;
    organizationName: string;
}

interface AddMemberModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddMembers?: (users: MockMember[]) => void;
    onSuccess?: () => void;
    organizationId?: number;
    currentUsers?: User[];
    licenseInfo?: LicenseInfo;
}

export default function AddMemberModal({
    isOpen,
    onClose,
    onAddMembers,
    onSuccess,
    organizationId,
    currentUsers = [],
    licenseInfo,
}: AddMemberModalProps): React.ReactElement {
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedUsers, setSelectedUsers] = useState<MockMember[]>([]);
    const [alertState, setAlertState] = useState({
        isOpen: false,
        type: 'success' as 'success' | 'error',
        title: '',
        message: '',
    });
    const queryClient = useQueryClient();

    // 정회원 목록 API — 모달 열리면 즉시 전체 로드, 검색어로 서버 필터링
    const { data: regularMemberData, isLoading } = useRegularMemberList(
        { page: 1, pageSize: 200, search: searchQuery.length >= 1 ? searchQuery : undefined },
        { enabled: isOpen }
    );

    const searchResults: MockMember[] = (regularMemberData?.userList ?? []).map((u) => ({
        userId: u.userId,
        name: u.realName,
        loginId: u.loginId ?? undefined,
        organizationName: '',
    }));

    // 기관 정회원 등록 API
    const registerMutation = useMutation({
        mutationFn: (userIdList: number[]) =>
            userServiceApi.registerOrganizationMembers({ organizationId: organizationId!, userIdList }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userManagement'] });
        },
    });

    const isUserAffiliated = (userId: number) =>
        currentUsers.some((u) => String(u.id) === String(userId));

    const isUserSelected = (userId: number) =>
        selectedUsers.some((u) => u.userId === userId);

    const toggleUserSelection = (user: MockMember) => {
        if (isUserAffiliated(user.userId)) return;
        setSelectedUsers((prev) =>
            prev.some((u) => u.userId === user.userId)
                ? prev.filter((u) => u.userId !== user.userId)
                : [...prev, user]
        );
    };

    const handleSubmit = async () => {
        if (selectedUsers.length === 0) {
            setAlertState({ isOpen: true, type: 'error', title: '회원 선택 필요', message: '추가할 회원을 선택해주세요.' });
            return;
        }

        try {
            if (organizationId) {
                await registerMutation.mutateAsync(selectedUsers.map((u) => u.userId));
            }
            onAddMembers?.(selectedUsers);
            setAlertState({
                isOpen: true,
                type: 'success',
                title: '정회원 추가 완료',
                message: `${selectedUsers.length}명의 회원이 추가되었습니다.`,
            });
            onSuccess?.();
            setTimeout(handleClose, 1200);
        } catch (err) {
            const msg = err instanceof Error ? err.message : '회원 추가에 실패했습니다.';
            setAlertState({ isOpen: true, type: 'error', title: '추가 실패', message: msg });
        }
    };

    const handleClose = () => {
        setSearchQuery('');
        setSelectedUsers([]);
        onClose();
    };

    const footer = (
        <>
            <Button variant="secondary" size="md" onClick={handleClose}>취소</Button>
            <Button
                variant="primary"
                size="md"
                onClick={handleSubmit}
                disabled={selectedUsers.length === 0 || registerMutation.isPending}
            >
                {selectedUsers.length > 0 ? `${selectedUsers.length}명 추가` : '확인'}
            </Button>
        </>
    );

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="정회원 추가" footer={footer} size="medium">
            <div className="space-y-4 min-h-[400px]">
                <div className="text-center bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-700 space-y-1">
                    {licenseInfo && (
                        <p className="text-sm text-brand-600 mb-2">
                            등록가능 사용자 수:{' '}
                            {licenseInfo.hasDeviceLicense
                                ? <span>제한 없음</span>
                                : <span>{Math.max(0, licenseInfo.availableSlots ?? 0)}명</span>
                            }
                        </p>
                    )}
                    <p>정회원 계정은 유효기간 제한없이 사용 가능합니다.</p>
                    <p>마스터 권한은 정회원만 가능합니다.</p>
                </div>

                <div className="space-y-2">
                    <div className="flex gap-2 items-end">
                        <Input
                            type="search"
                            id="user-search-input"
                            label="회원 검색"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="이름 또는 ID 입력"
                            aria-label="회원 검색"
                            className="flex-1"
                        />
                    </div>
                    {!searchQuery && (
                        <p className="text-xs text-gray-500">이름 또는 ID로 검색하세요</p>
                    )}
                </div>

                {isLoading && (
                    <div className="text-center py-4 text-gray-500 text-sm">불러오는 중...</div>
                )}

                {!isLoading && searchResults.length > 0 && (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            {searchQuery ? `검색 결과 (${searchResults.length}명)` : `전체 정회원 (${searchResults.length}명)`}
                        </label>
                        <div className="border border-gray-200 rounded-lg max-h-[240px] overflow-y-auto">
                            {searchResults.map((user) => {
                                const isAffiliated = isUserAffiliated(user.userId);
                                const isSelected = isUserSelected(user.userId);
                                return (
                                    <label
                                        key={user.userId}
                                        className={`flex items-center p-3 border-b border-gray-200 last:border-b-0 transition-colors ${
                                            isAffiliated ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'
                                        } ${isSelected ? 'bg-blue-50' : ''}`}
                                        onClick={() => !isAffiliated && toggleUserSelection(user)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            disabled={isAffiliated}
                                            onChange={() => {}}
                                            className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 font-medium">
                                                {user.name} ({user.loginId || '-'})
                                                {isAffiliated && (
                                                    <span className="flex items-center gap-1 text-xs text-blue-600">
                                                        <CheckCircle size={14} />소속됨
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!isLoading && searchResults.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        {searchQuery ? '검색 결과가 없습니다.' : '등록 가능한 정회원이 없습니다.'}
                    </div>
                )}

                {selectedUsers.length > 0 && (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            선택된 사용자 ({selectedUsers.length}명)
                        </label>
                        <div className="bg-gray-50 p-3 rounded-lg flex flex-wrap gap-2">
                            {selectedUsers.map((user) => (
                                <Badge
                                    key={user.userId}
                                    variant="default"
                                    className="cursor-pointer hover:bg-blue-700"
                                    onClick={() => toggleUserSelection(user)}
                                >
                                    {user.name} ✕
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <AlertDialog
                isOpen={alertState.isOpen}
                onClose={() => setAlertState((prev) => ({ ...prev, isOpen: false }))}
                title={alertState.title}
                message={alertState.message}
            />
        </Modal>
    );
}
